import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackGA4Event } from "@/lib/tracking";
import type { AppShellTranslations } from "@/components/app/i18n/shell";
import { NavItem } from "./NavItem";
import { isPathActive, type NavItemConfig } from "./nav-config";
import type { SidebarClasses } from "./sidebar-theme";

type NavLabels = AppShellTranslations["sidebar"]["nav"];

interface NavGroupProps {
  label: string;
  items: readonly NavItemConfig[];
  collapsed: boolean;
  pathname: string;
  labels: NavLabels;
  classes: SidebarClasses;
  onNavigate?: () => void;
}

/** Grupo do menu: rótulo em caixa alta (expandido) ou separador (recolhido), figma-spec §4.4 e §6. */
export function NavGroup({ label, items, collapsed, pathname, labels, classes, onNavigate }: NavGroupProps) {
  const handleClick = (item: NavItemConfig) => () => {
    if (item.trackingEvent) trackGA4Event(item.trackingEvent);
    onNavigate?.();
  };

  if (collapsed) {
    return (
      <div role="group" aria-label={label} className="flex flex-col items-center gap-1">
        <span aria-hidden="true" className="flex h-[17px] items-center">
          <span className={cn("block h-px w-6", classes.divider)} />
        </span>
        {items.map((item) => {
          const childActive = item.children?.some((c) => isPathActive(pathname, c.path)) ?? false;
          return (
            <NavItem
              key={item.path}
              variant="rail"
              label={labels[item.labelKey]}
              to={item.path}
              icon={item.icon}
              active={isPathActive(pathname, item.path) || childActive}
              classes={classes}
              onClick={handleClick(item)}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div role="group" aria-label={label} className="flex flex-col gap-0.5">
      <p
        aria-hidden="true"
        className={cn(
          "px-2.5 pb-1.5 pt-3.5 text-[10.5px] font-semibold uppercase leading-[14px] tracking-[0.8px]",
          classes.groupLabel,
        )}
      >
        {label}
      </p>
      {items.map((item) => {
        const children = item.children ?? [];
        const childActive = children.some((c) => isPathActive(pathname, c.path));
        const selfActive = isPathActive(pathname, item.path);
        // S6: subitens abertos quando a seção (pai ou filho) está ativa, como no menu anterior.
        const open = (selfActive || childActive) && children.length > 0;
        const Chevron = open ? ChevronUp : ChevronDown;
        return (
          <div key={item.path} className="flex flex-col gap-0.5">
            <NavItem
              variant="item"
              label={labels[item.labelKey]}
              to={item.path}
              icon={item.icon}
              active={selfActive && !childActive}
              classes={classes}
              onClick={handleClick(item)}
              trailing={
                children.length > 0 ? (
                  <Chevron className={cn("h-4 w-4 shrink-0", classes.icon)} strokeWidth={1.75} aria-hidden="true" />
                ) : undefined
              }
            />
            {open &&
              children.map((child) => (
                <NavItem
                  key={child.path}
                  variant="subitem"
                  label={labels[child.labelKey]}
                  to={child.path}
                  active={isPathActive(pathname, child.path)}
                  classes={classes}
                  onClick={onNavigate}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}

/** Skeleton dos itens enquanto `usePermissions` carrega (S17). */
export function NavSkeleton({
  collapsed,
  classes,
  label,
}: {
  collapsed: boolean;
  classes: SidebarClasses;
  label: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn("flex flex-col gap-2 pt-3.5", collapsed ? "items-center" : "px-2.5")}
    >
      {Array.from({ length: 8 }, (_, i) => (
        <span
          key={i}
          className={cn("block animate-pulse rounded-[8px]", classes.skeleton, collapsed ? "h-10 w-12" : "h-7 w-full")}
        />
      ))}
    </div>
  );
}
